import React, { createContext, useMemo, ReactNode } from 'react';
import { Maybe, UserPublicConsentKeysForEventDocument, useSetUserConsentForEventMutation, useUserPublicConsentKeysForEventQuery } from '@graphql/generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { UserConsent, UserConsentKey, UserConsentMetadata } from './useUserConsent.types';
import { useEventInfo } from '@shared/utils/eventInfo';

export const UserConsentContext = createContext<UserConsent | undefined>(undefined);

export const UserConsentProvider = ({ children }: { children: ReactNode }) => {
  const { eventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId;

  const shouldSkip = !eventId;

  const { data, loading: loadingUserConsent } = useUserPublicConsentKeysForEventQuery({
    variables: { eventId: eventId! },
    batchMode: 'fast',
    skip: shouldSkip,
  });

  const [updateUserConsent] = useSetUserConsentForEventMutation();

  const consentMap = useMemo(() => {
    const entries: ReadonlyArray<[UserConsentKey, { answer: boolean; createdAt?: Maybe<Date> }]> =
      data?.userPublicConsentKeysForEvent?.map(item => [
        item.consentKey as UserConsentKey,
        { answer: item.consentAnswer, createdAt: typeof item.createdAt === 'string' ? new Date(item.createdAt) : undefined },
      ]) ?? [];
    return new Map<UserConsentKey, { answer: boolean; createdAt?: Maybe<Date> }>(entries);
  }, [data]);

  const getUserConsent = useEventCallback((consentKey: UserConsentKey, options?: { consentKeyUsesMetadata?: boolean }): boolean | undefined | UserConsentMetadata => {
    const consent = consentMap.get(consentKey);
    if (options?.consentKeyUsesMetadata) {
      return { answer: consent?.answer, createdAt: consent?.createdAt };
    }
    return consent?.answer;
  }) as UserConsent['getUserConsent'];

  const setUserConsent = useEventCallback(async (consentKey: UserConsentKey, consentAnswer: boolean) => {
    if (!eventId) return;

    await updateUserConsent({
      variables: {
        data: {
          eventId,
          consentKey,
          consentAnswer,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: UserPublicConsentKeysForEventDocument,
          variables: {
            eventId,
          },
        },
      ],
    });
  });

  return <UserConsentContext.Provider value={{ getUserConsent, setUserConsent, loadingUserConsent }}>{children}</UserConsentContext.Provider>;
};
